import { isEqual } from 'lodash';
import { get as httpGet, patch as httpPatch } from './BackendService';

const DAY_IN_MS = 24 * 60 * 60 * 1000;

export type PlanId =
  | 'basic-001'
  | 'pro-001'
  | 'startup-001'
  | 'business-001'
  | 'enterprise-001'
  | 'startup-2022'
  | 'scaleup-2022'
  | 'enterprise-2022'
  | 'plan-2024-050'
  | 'plan-2024-100'
  | 'plan-2024-200'
  | 'plan-2024-300'
  | 'custom-enterprise-001'
  | 'custom-enterprise-yearly-001'
  | 'starter-2025'
  | 'growth-2025'
  | 'pro-2025'
  | 'enterprise-plus-2025'
  | 'poc-2025';

export type RenderRegion = 'us' | 'eu' | 'shared';

export type ExtractHiddenCssLocation = 'top' | 'bottom';

export interface RenderLog {
  type: string;
  factor: string;
  start: string;
  end: string;
  meta?: { logUrl: string };
}

export interface ScrollSettings {
  readonly enabled: boolean;
  readonly distance?: number;
  readonly delay?: number;
  readonly repeatDelay?: number;
  readonly noOverscroll?: boolean;
  readonly speed?: number;
  readonly x?: number;
  readonly y?: number;
}

export interface NormalizeUriSettings {
  readonly doNotDecodeUri?: string[];
  readonly onlyOneDecodeUri?: string[];
  readonly onlyOneDecodeUriComponent?: string[];
  readonly toLowerCase?: string[];
  readonly urlMustBeDecoded?: string[];
  readonly useP20InQuery?: string[];
  readonly restoreSearchParams?: boolean;
  readonly encodedBrackets?: boolean;
  readonly sortQueryParameters?: boolean;
}

export interface UserSettings {
  readonly forceHTTPS: boolean;
  readonly customViewPort: ViewPort | null;
  readonly customMobileViewport: ViewPort | null;
  readonly loadImages: boolean;
  readonly renderRegion: RenderRegion;
  readonly skipCustomElementsForcePolyfill: boolean;
  readonly removeStyleTags: boolean;
  readonly removeURLHash: boolean;
  readonly enableServiceWorker: boolean;
  readonly pageLoadTimeout: number;
  readonly parseShadowDom: boolean;
  readonly removePrefetch: boolean;
  readonly removePreload: boolean;
  readonly retryTimedoutRenders: boolean;
  readonly retryDirtyRenders: boolean;
  readonly manualReactivate: boolean;
  readonly addBlockedResources: Array<string>;
  readonly removeBlockedResources: Array<string>;
  readonly renderLogs: Array<RenderLog>;
  readonly scroll: ScrollSettings;
  readonly preCheckStatus: boolean;
  readonly normalizeUri: NormalizeUriSettings;
  readonly extractHiddenCssLocation: ExtractHiddenCssLocation;
  readonly fixHeadTag: boolean;
  readonly forceApiAccess: boolean;
  readonly scrollToBottomAfterPageLoad: boolean;
}

export interface ViewPort {
  readonly width: number;
  readonly height: number;
}

export interface Environment {
  readonly cacheFreshness: number;
  readonly customRecacheDelay: number | null;
  readonly minimumBodyLength: number;
  readonly slowRenderTriggerSeconds: number;
}

export interface CSM {
  readonly id: string;
  readonly title: string;
  readonly email: string;
  readonly name: string;
  readonly picture: string;
  readonly meeting_url: string;
}

export interface UserResult {
  readonly id: number;
  readonly email: string;
  readonly companyName: string;
  readonly adminEmail: string;
  readonly apiToken?: string;
  readonly isMetered: boolean;
  readonly numCached: number;
  readonly allUrlCount: number;
  readonly renderCounter: number;
  readonly isCustomPricing: boolean;
  readonly chargebeePlanId: string;
  readonly chargebeeCustomerId: string;
  readonly chargebeeFilter: string;
  readonly customCachedPagesLimit: number | null;
  readonly chargingThroughChargebee: boolean;
  readonly mobileAdaptive: boolean;
  readonly importance: number;
  readonly recachingDisabled: boolean;
  readonly userPausedRecaching: boolean;
  readonly cancelledAt: string | null;
  readonly lastCharged: string | null;
  readonly endOfFutureRenewal: string | null;
  readonly customPrice: number | null;
  readonly plan: {
    readonly chargeBeePlanId?: PlanId | null;
  };
  readonly settings: UserSettings;
  readonly unlimRendersUntil: string | null;
  readonly betaTester: boolean;
  readonly cachedNumCached: number;
  readonly cachedRenderCounter: number;
  readonly cachedAllUrlCount: number;
  readonly ignoreAllQueryParams: boolean;
  readonly whitelistQueryParams: boolean;
  readonly extractHiddenCss: boolean;
  readonly environment: Environment;
  readonly onlyServeCacheHits: boolean;
  readonly csmId: string;
  readonly trackingCodeInstalled: boolean;
  readonly availableFeatures: string[];
}

interface CalculatedUserFields {
  readonly circleSrc: string;
  readonly cancelled: boolean;
  readonly delayBetweenURLs: number;
  readonly hasUnlimRenders: boolean;
}
export type User = UserResult & CalculatedUserFields;

export interface LoginUser {
  readonly id: number;
  readonly email: string;
  readonly userId: number;
  readonly emailVerificationLink: string;
  readonly passwordResetLink: string;
  readonly pendingInvitation: boolean;
  readonly role: 'superAdmin' | 'admin' | 'billingManager' | 'teamMember' | 'guest';
  readonly phoneNumber?: string;
}

export type LoginUserWithAccount = Omit<User, 'id'> & Omit<LoginUser, 'id'> & { userId: number; loginUserId: number };

export type UpdatableFields = Pick<
  User,
  | 'adminEmail'
  | 'companyName'
  | 'unlimRendersUntil'
  | 'renderCounter'
  | 'settings'
  | 'mobileAdaptive'
  | 'recachingDisabled'
  | 'betaTester'
  | 'userPausedRecaching'
  | 'ignoreAllQueryParams'
  | 'whitelistQueryParams'
  | 'extractHiddenCss'
  | 'environment'
  | 'onlyServeCacheHits'
  | 'csmId'
  | 'trackingCodeInstalled'
  | 'availableFeatures'
>;

export const getCircleSrc = (importance: number): string => {
  const base = '/images/';
  switch (importance) {
    case 5:
    case 4:
      return `${base}redCircle.svg`;
    case 3:
    case 2:
      return `${base}yellowCircle.svg`;

    case 0:
    case 1:
    default:
      return `${base}greenCircle.svg`;
  }
};

const createUser = (user: UserResult): User => {
  return {
    ...user,
    cancelled: !!user.cancelledAt,
    circleSrc: getCircleSrc(user.importance),
    delayBetweenURLs: getDelayBetweenUrls(
      user.environment.cacheFreshness,
      user.allUrlCount,
      user.environment.customRecacheDelay
    ),
    hasUnlimRenders: !!user.unlimRendersUntil,
  };
};

export const getUpdatableFields = (user: User): UpdatableFields => {
  return {
    adminEmail: user.adminEmail,
    companyName: user.companyName,
    unlimRendersUntil: user.unlimRendersUntil,
    renderCounter: user.renderCounter,
    settings: user.settings,
    mobileAdaptive: user.mobileAdaptive,
    recachingDisabled: user.recachingDisabled,
    betaTester: user.betaTester,
    userPausedRecaching: user.userPausedRecaching,
    ignoreAllQueryParams: user.ignoreAllQueryParams,
    whitelistQueryParams: user.whitelistQueryParams,
    extractHiddenCss: user.extractHiddenCss,
    onlyServeCacheHits: user.onlyServeCacheHits,
    environment: {
      customRecacheDelay: user.environment.customRecacheDelay,
      cacheFreshness: user.environment.cacheFreshness,
      minimumBodyLength: user.environment.minimumBodyLength,
      slowRenderTriggerSeconds: user.environment.slowRenderTriggerSeconds,
    },
    csmId: user.csmId,
    trackingCodeInstalled: user.trackingCodeInstalled,
    availableFeatures: user.availableFeatures,
  };
};

interface UserUpdateData {
  readonly adminEmail?: string;
  readonly companyName?: string;
  readonly renderCounter?: number;
  readonly unlimRendersUntil?: string | null;
  readonly betaTester?: boolean;
  readonly settings?: UserSettings;
  readonly environment?: Partial<Environment>;
  readonly mobileAdaptive?: boolean;
  readonly recachingDisabled?: boolean;
  readonly userPausedRecaching?: boolean;
  readonly ignoreAllQueryParams?: boolean;
  readonly whitelistQueryParams?: boolean;
  readonly extractHiddenCss?: boolean;
  readonly onlyServeCacheHits?: boolean;
  readonly csmId?: string;
  readonly trackingCodeInstalled?: boolean;
  readonly availableFeatures?: string[];
}

interface LoginUserUpdateData {
  readonly email?: string;
}

export const getDiff = (user: User, originalUser: User): UserUpdateData => {
  const result: UserUpdateData = {
    adminEmail: user.adminEmail !== originalUser.adminEmail ? user.adminEmail : undefined,
    companyName: user.companyName !== originalUser.companyName ? user.companyName : undefined,
    renderCounter: user.renderCounter !== originalUser.renderCounter ? user.renderCounter : undefined,
    unlimRendersUntil: user.unlimRendersUntil !== originalUser.unlimRendersUntil ? user.unlimRendersUntil : undefined,
    betaTester: user.betaTester !== originalUser.betaTester ? user.betaTester : undefined,
    settings: !isEqual(user.settings, originalUser.settings) ? user.settings : undefined,
    environment: {
      cacheFreshness:
        user.environment.cacheFreshness !== originalUser.environment.cacheFreshness
          ? user.environment.cacheFreshness
          : undefined,
      customRecacheDelay:
        user.environment.customRecacheDelay !== originalUser.environment.customRecacheDelay
          ? user.environment.customRecacheDelay
          : undefined,
      minimumBodyLength:
        user.environment.minimumBodyLength !== originalUser.environment.minimumBodyLength
          ? user.environment.minimumBodyLength
          : undefined,
      slowRenderTriggerSeconds:
        user.environment.slowRenderTriggerSeconds !== originalUser.environment.slowRenderTriggerSeconds
          ? user.environment.slowRenderTriggerSeconds
          : undefined,
    },
    mobileAdaptive: user.mobileAdaptive !== originalUser.mobileAdaptive ? user.mobileAdaptive : undefined,
    recachingDisabled: user.recachingDisabled !== originalUser.recachingDisabled ? user.recachingDisabled : undefined,
    userPausedRecaching:
      user.userPausedRecaching !== originalUser.userPausedRecaching ? user.userPausedRecaching : undefined,
    ignoreAllQueryParams:
      user.ignoreAllQueryParams !== originalUser.ignoreAllQueryParams ? user.ignoreAllQueryParams : undefined,
    whitelistQueryParams:
      user.whitelistQueryParams !== originalUser.whitelistQueryParams ? user.whitelistQueryParams : undefined,
    extractHiddenCss: user.extractHiddenCss !== originalUser.extractHiddenCss ? user.extractHiddenCss : undefined,
    onlyServeCacheHits:
      user.onlyServeCacheHits !== originalUser.onlyServeCacheHits ? user.onlyServeCacheHits : undefined,
    csmId: user.csmId !== originalUser.csmId ? user.csmId : undefined,
    trackingCodeInstalled:
      user.trackingCodeInstalled !== originalUser.trackingCodeInstalled ? user.trackingCodeInstalled : undefined,
    availableFeatures: !isEqual(user.availableFeatures, originalUser.availableFeatures)
      ? user.availableFeatures
      : undefined,
  };

  Object.keys(result).forEach((key) => (result as any)[key] === undefined && delete (result as any)[key]);

  return result;
};

export const list = async (offset: number, limit: number, search: string | null = null): Promise<Array<User>> => {
  const searchParams = new URLSearchParams({
    offset: offset.toString(),
    limit: limit.toString(),
  });

  if (search) searchParams.append('search', search);
  const result: Array<UserResult> | null = await httpGet<Array<UserResult>>(`/users?${searchParams.toString()}`);
  return result.map(createUser) || [];
};

export const get = async (id: number): Promise<User> => {
  const user = await httpGet<UserResult>(`/users/${id}`);

  return createUser(user);
};

export const update = async (id: number, update: UserUpdateData): Promise<User> => {
  const result = await httpPatch<UserResult>(`/users/${id}`, update);

  return createUser(result);
};

export const getDelayBetweenUrls = (
  cacheFreshness: number,
  allUrlCount: number,
  customRecacheDelay: number | null = null
): number => {
  let delayBetweenURLs = Math.floor((cacheFreshness * DAY_IN_MS) / (allUrlCount || 1));
  if (customRecacheDelay !== null) delayBetweenURLs = customRecacheDelay;

  return delayBetweenURLs;
};

export const getLoginUsers = (userId: number): Promise<LoginUser[]> => {
  return httpGet<LoginUser[]>(`/users/${userId}/loginUsers`);
};

export const anonymizeLoginUser = async (loginUserId: number): Promise<void> => {
  await httpPatch(`/loginUser/${loginUserId}/anonymize`, undefined);
};

export const updateLoginUser = async (loginUserId: number, update: LoginUserUpdateData): Promise<LoginUser> => {
  return await httpPatch<LoginUser>(`/loginUser/${loginUserId}`, update);
};

export const anonymizeUser = async (userId: number): Promise<void> => {
  await httpPatch(`/users/${userId}/anonymize`, undefined);
};

export const moveLoginUser = async (loginUserId: number, targetUserId: number, role: LoginUser['role']): Promise<void> => {
  await httpPatch(`/loginUser/${loginUserId}`, { userId: targetUserId, role });
};
